import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    TextField,
} from '@mui/material';
import WhiteboardNote from './WhiteboardNote';
import UserApi from '../../../../rest-api/user-api';
import FavouriteButtonActive from '../../../../assets/icons/FavouriteButtonActive.svg';
import FavouriteButtonDisabled from '../../../../assets/icons/FavouriteButtonDisabled.svg';
import PinkGradientButton from '../../../UIControls/PinkGradientButton';
const CARD_WIDTH = 369;
const CARD_HEIGHT = 600;
const location = 'en-GB';

const WhiteboardCard = ({
    whiteboard,
    isMobile,
    userDataStore,
    authStore,
    whiteboardStore,
    showSnackbar,
    allowAddNote = true,
    handleToggleFavourite,
    handleEditWhiteboard,
    handleArchiveWhiteboard,
    imageMap,
    index,
    actionLabel = 'Archive', // defaults keep My Library unchanged
    confirmTitle = 'Are you sure you want to archive this whiteboard?',
    confirmBody = 'This action will archive the whiteboard and remove it from your list.',
    hideFavourite = false,
    hideEdit = false,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteDraft, setNoteDraft] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [notesValue, setNotesValue] = useState(whiteboard.notes || '');

    // keep local notes in sync if parent/store updates
    useEffect(() => {
        setNotesValue(whiteboard.notes || '');
    }, [whiteboard.notes]);

    // console.log('WhiteboardCard rendered for:', whiteboard.whiteboardId, index);

    const handleConfirmArchive = () => {
        handleArchiveWhiteboard(whiteboard);
        setConfirmOpen(false);
    };

    const openAddNote = () => {
        setNoteDraft('');
        setNoteDialogOpen(true);
    };

    const saveNote = async () => {
        if (savingNote) return;

        if (!authStore?.idToken) {
            console.error('authStore.idToken is missing – cannot save note.');
            showSnackbar && showSnackbar('Unable to save note (not signed in).', 'error');
            return;
        }

        setSavingNote(true);
        try {
            const payload = {
                whiteboardId: whiteboard.whiteboardId,
                fileName: whiteboard.fileName, // required by schema
                notes: noteDraft,
                updatedById: userDataStore.userId,
            };
            await UserApi.createOrUpdateWhiteboard(authStore.idToken, payload);

            // Update local store so UI reflects the change without a refetch
            // Extracting lastUsedColours is for protecting against Cloning err (PUT method in store DB)
            const { lastUsedColours, ...rest } = whiteboard;
            const updated = {
                ...rest,
                notes: noteDraft,
            };

            await whiteboardStore.setWhiteboardData([updated]);
            whiteboardStore.whiteboardData = {
                ...whiteboardStore.whiteboardData,
                [updated.whiteboardId]: updated,
            };

            // Immediate local UI update
            setNotesValue(noteDraft);

            showSnackbar && showSnackbar('Note added', 'success');
            setNoteDialogOpen(false);
        } catch (err) {
            console.error('Failed to save whiteboard note', err);
            showSnackbar && showSnackbar('Failed to save note', 'error');
        } finally {
            setSavingNote(false);
        }
    };

    return (
        <Grid
            item
            xs={12}
            md={4}
            key={whiteboard.whiteboardId || index}
            pl={index % 3 === 0 ? '0!important' : isMobile ? '0!important' : 2}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {/* Handout Name */}
                <Box sx={{ width: isMobile ? '100%' : `${CARD_WIDTH}px` }}>
                    <Tooltip title={whiteboard.handoutContentName || 'No Title'} arrow>
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontSize: '22px',
                                fontWeight: '600',
                                color: '#000453',
                                textAlign: 'left',
                                mt: '10px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {whiteboard.handoutContentName || 'No Title'}
                        </Typography>
                    </Tooltip>
                </Box>

                {/* Whiteboard Panel */}
                <Box
                    sx={{
                        width: '100%',
                        height: `${CARD_HEIGHT}px`,
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        display: 'flex',
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                        mt: '20px',
                        position: 'relative',
                        flexDirection: 'column',
                    }}
                >
                    {/* Favourite Icon */}
                    {!hideFavourite && (
                        <IconButton
                            onClick={() => handleToggleFavourite(whiteboard.whiteboardId)}
                            sx={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                width: '32px',
                                height: '32px',
                                padding: 0,
                            }}
                        >
                            <img
                                src={
                                    userDataStore.userData.whiteboardsFavourites?.includes(whiteboard.whiteboardId)
                                        ? FavouriteButtonActive
                                        : FavouriteButtonDisabled
                                }
                                alt="Favourite"
                                style={{ width: '32px', height: '32px' }}
                            />
                        </IconButton>
                    )}

                    {/* Updated on / By */}
                    <Box sx={{ px: 2, pt: 2, width: '90%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '22px',
                                color: '#10141F66',
                                minHeight: '26px',
                                lineHeight: '26px',
                                whiteSpace: 'normal',
                                overflowWrap: 'break-word',
                            }}
                        >
                            Updated on: {new Date(whiteboard.lastUpdated).toLocaleString(location)}
                        </Typography>

                        {userDataStore?.subscriptionData?.accountType?.includes('Clinic') &&
                            (whiteboard.updatedByFirstName || whiteboard.updatedByLastName) && (
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 500,
                                        fontSize: '22px',
                                        color: '#10141F66',
                                        mt: 0,
                                        minHeight: '26px',
                                        lineHeight: '26px',
                                        whiteSpace: 'normal',
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    By:{' '}
                                    {[whiteboard.updatedByFirstName, whiteboard.updatedByLastName]
                                        .filter(Boolean)
                                        .join(' ')}
                                </Typography>
                            )}
                    </Box>

                    {/* Image Preview */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                        }}
                    >
                        {whiteboard.canvasImageLink && imageMap[whiteboard.canvasImageLink] ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    maxHeight: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <img
                                    src={imageMap[whiteboard.canvasImageLink]}
                                    alt={whiteboard.handoutContentName || 'Whiteboard Preview'}
                                    style={{ width: '100%', height: '800%', objectFit: 'contain', maxWidth: '420px' }}
                                />
                            </Box>
                        ) : (
                            <Typography color="textSecondary">No preview available</Typography>
                        )}
                    </Box>

                    {/* Notes */}
                    <Box sx={{ px: 2, pb: 2 }}>
                        <WhiteboardNote notes={notesValue} />
                        {allowAddNote && !notesValue && (
                            <Button
                                variant="outlined"
                                onClick={openAddNote}
                                sx={{
                                    mt: 1,
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                }}
                            >
                                Add note
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Buttons */}
                <Box
                    component={'section'}
                    sx={{
                        display: 'flex',
                        gap: '12px',
                        width: '100%',
                        mt: '16px',
                        mb: '48px',
                    }}
                >
                    {!hideEdit && (
                        <PinkGradientButton
                            variant="outlined"
                            onClick={() => handleEditWhiteboard(whiteboard)}
                            sx={{
                                height: '40px',
                                borderRadius: '12px',
                                color: 'white',
                                boxShadow: 'none',
                                fontFamily: 'Inter',
                                width: '50%',
                                m: 0,
                            }}
                        >
                            Edit
                        </PinkGradientButton>
                    )}

                    <Button
                        variant="contained"
                        fullWidth={hideEdit}
                        sx={{
                            flex: 1,
                            height: '40px',
                            borderRadius: '12px',
                            backgroundColor: '#000453',
                            color: 'white',
                            boxShadow: 'none',
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            width: '50%',
                            '&:hover': {
                                backgroundColor: '#000331',
                            },
                        }}
                        onClick={() => setConfirmOpen(true)}
                    >
                        {actionLabel}
                    </Button>
                </Box>

                {/* Confirmation Dialog */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>{confirmTitle}</DialogTitle>
                    <DialogContent>
                        <Typography>{confirmBody}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConfirmArchive} color="primary" autoFocus>
                            Yes
                        </Button>
                        <Button onClick={() => setConfirmOpen(false)}>No</Button>
                    </DialogActions>
                </Dialog>
                {/* Add Note Dialog */}
                <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Add note</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            minRows={3}
                            placeholder="Type a quick note that will be saved with this whiteboard…"
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            margin="normal"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={saveNote} variant="contained" disabled={savingNote || !noteDraft.trim()}>
                            Save note
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Grid>
    );
};

export default observer(WhiteboardCard);
